/**
 * One-shot attack FX sprites via ArtPipeline (affiliation rim baked).
 * Particles stay in VfxPlayer — this is the Duelyst-style FX sheet layer.
 */
import type Phaser from "phaser";
import { ArtPipeline } from "../art";

export type SpriteFxPlayOpts = {
  packId: string;
  spriteId: string;
  /** Affiliation key for rim bake (ally/enemy/…). */
  affiliation: string;
  x: number;
  y: number;
  /** Aim radians; FX authored facing east (0). */
  angle?: number;
  scale?: number;
  depth?: number;
  /** Override clip fps timing (default: authored idle fps). */
  fps?: number;
};

/**
 * Play a short FX sprite clip above the unit, then destroy.
 * Uses idle clip frames from the FX sprite (east facing + rotation).
 */
export function playSpriteFx(
  scene: Phaser.Scene,
  opts: SpriteFxPlayOpts,
): void {
  const {
    packId,
    spriteId,
    affiliation,
    x,
    y,
    angle = 0,
    scale = 1,
    depth = 5,
  } = opts;

  if (!ArtPipeline.hasPack(packId)) return;
  const pack = ArtPipeline.getPack(packId);
  if (!pack.sprites[spriteId] || !pack.anims[spriteId]) return;

  const duration = ArtPipeline.animClipDuration(packId, spriteId, "idle", "e");
  const { key } = ArtPipeline.ensureAnimFrame(
    scene,
    packId,
    spriteId,
    affiliation,
    "idle",
    0,
    "e",
  );

  const img = scene.add.image(x, y, key);
  img.setDepth(depth);
  img.setScale(scale);
  img.setRotation(angle);
  img.setOrigin(0.5, 0.5);

  let elapsed = 0;
  const tick = (_time: number, dtMs: number) => {
    if (!img.active) {
      scene.events.off("update", tick);
      return;
    }
    elapsed += dtMs / 1000;
    const { key: nextKey, clipDone } = ArtPipeline.ensureAnimFrame(
      scene,
      packId,
      spriteId,
      affiliation,
      "idle",
      elapsed,
      "e",
    );
    if (img.texture.key !== nextKey) {
      img.setTexture(nextKey);
    }
    if (clipDone || elapsed >= duration + 0.02) {
      scene.events.off("update", tick);
      img.destroy();
    }
  };
  scene.events.on("update", tick);
}
